import React, { useEffect, useRef } from "react";
import Lenis from "lenis";

interface SmoothScrollProps {
  children: React.ReactNode;
}

export const SmoothScroll: React.FC<SmoothScrollProps> = ({ children }) => {
  const lenisRef = useRef<Lenis | null>(null);

  useEffect(() => {
    // Respect the OS "Reduce motion" setting: Lenis hijacks the native scroll
    // (momentum + eased wheel), which is exactly the kind of motion that setting
    // asks us to drop. When the user prefers reduced motion we skip Lenis entirely
    // and leave the browser's own scrolling in place. WCAG 2.3.3 (LL-0019).
    if (
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }

    // Initialize Lenis
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: "vertical",
      gestureOrientation: "vertical",
      smoothWheel: true,
      touchMultiplier: 2,
    });

    lenisRef.current = lenis;
    // Exposed so route changes can reset the scroll position. Lenis hijacks
    // native scrolling, so a plain window.scrollTo is not reliably honoured
    // while it is running: navigating from the Loop In pill to /download landed
    // part-way down the page because of exactly that.
    (window as unknown as { __lenis?: Lenis }).__lenis = lenis;

    // RAF loop
    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  return <div className="w-full min-h-screen">{children}</div>;
};
